import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import {
  Box,
  EditableText,
  Icon,
  useAlert,
  useToast,
} from "@prismicio/editor-ui"
import { useState } from "react"
import { z } from "zod"

import { staticEditorConfig } from "../../app/editorConfig"
import { updateReleasesListData } from "../../app/services/release"

interface ReleaseNameInputProps {
  defaultValue?: string
  releaseId: string
}

export function ReleaseNameInput(props: ReleaseNameInputProps) {
  const { defaultValue, releaseId } = props
  const [value, setValue] = useState(defaultValue || "")
  const [loading, setLoading] = useState(false)

  const { showAlert } = useAlert()
  const { showToast } = useToast()

  function onBlur() {
    if (value === defaultValue) return

    setLoading(true)
    updateReleaseName({ releaseId, name: value })
      .then(() => {
        setLoading(false)
        updateReleasesListData((releases) => {
          // update cached release name
          return releases.map((release) =>
            release.id === releaseId ? { ...release, label: value } : release,
          )
        })
        showToast({
          title: "Release has been successfully renamed",
        })
      })
      .catch(() => {
        setLoading(false)
        setValue(defaultValue || "")
        showAlert({
          title: "Error",
          subtitle: `Something went wrong when renaming ${
            defaultValue ?? "..."
          }.`,
        })
      })
  }

  return (
    <Box gap={4} alignItems="center">
      <EditableText
        aria-label="Edit Release Name"
        value={value}
        variant="h3"
        color="grey12"
        onBlur={onBlur}
        onChange={setValue}
        disabled={loading}
      />
      <Icon name="edit" size="medium" color="grey11" />
    </Box>
  )
}

interface updateReleaseNameArgs {
  releaseId: string
  name: string
}

function updateReleaseName(args: updateReleaseNameArgs) {
  const { releaseId, name } = args

  const { authStrategy, coreApiBaseUrl } = staticEditorConfig

  return request(new URL(`releases/${releaseId}/rename`, coreApiBaseUrl), {
    body: {
      name,
    },
    method: "POST",
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown(),
  })
}
